import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import LayerOrganizeSortableRow from "@/v2-backoffice-client/layer/organize/LayerOrganizeSortableRow"
import { useLayerOrganizeDragSensors } from "@/v2-backoffice-client/layer/organize/layer.organize.dnd"
import type { LayerFragment } from "@/v2-backoffice-client/layer/__types__/layer.fragment"

export type LayerOrganizeDrawOrderTabProps = {
  /** Every layer code, ascending Priority (= the chooser's top-to-bottom). */
  globalOrder: string[]
  layersByCode: Map<string, LayerFragment>
  onGlobalOrderChange(order: string[]): void
}

/**
 * The "Draw order" tab of the organizer: the one flat global ranking, as the
 * map stacks it — rendered descending, so the top of the list is drawn on
 * top of the map and the bottom is drawn first, underneath everything else.
 *
 * Owns the drag handling (mapping the reversed render order back into the
 * ascending ranking); the pending order itself lives in the dialog because
 * it is shared with the categories tab and committed by the dialog's single
 * Save button.
 */
function LayerOrganizeDrawOrderTab(props: LayerOrganizeDrawOrderTabProps) {
  const { globalOrder, layersByCode, onGlobalOrderChange } = props

  const sensors = useLayerOrganizeDragSensors()

  function onDrawDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    // The draw tab renders descending, so map its indexes back.
    const reversed = [...globalOrder].reverse()
    const from = reversed.indexOf(String(active.id))
    const to = reversed.indexOf(String(over.id))
    if (from < 0 || to < 0) return
    onGlobalOrderChange(arrayMove(reversed, from, to).reverse())
  }

  return (
    <>
      <p className="pb-2 text-xs text-muted-foreground">
        The one global ranking, as the map stacks it: the top of the list is
        drawn on top, the bottom is drawn first, underneath everything else.
      </p>
      <div className="flex max-h-[55vh] flex-col gap-1 overflow-y-auto pr-1">
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Drawn on top
        </p>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDrawDragEnd}
        >
          <SortableContext
            items={[...globalOrder].reverse()}
            strategy={verticalListSortingStrategy}
          >
            <div role="list" className="flex flex-col gap-1">
              {[...globalOrder].reverse().map((code, index) => {
                const layer = layersByCode.get(code)
                if (!layer) return null
                return (
                  <div key={code} role="listitem">
                    <LayerOrganizeSortableRow id={code}>
                      <span className="w-8 shrink-0 tabular-nums text-muted-foreground">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1 truncate">
                        {layer.Name}
                        <span className="ml-2 text-muted-foreground">
                          {layer.FeatureCode}
                        </span>
                      </span>
                    </LayerOrganizeSortableRow>
                  </div>
                )
              })}
            </div>
          </SortableContext>
        </DndContext>
        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
          Drawn first (bottom)
        </p>
      </div>
    </>
  )
}

export default LayerOrganizeDrawOrderTab
